import React from "react";
import "../ui/ui.scss";

/**
 * How many ramp steps `.ui-avatar--color-N` covers.
 *
 * ⚠️ This was a 16-element array of hexes, and NONE of its values were ever
 * read — only `.length`, to modulo the name hash. The colours live in
 * `--avatar-1..16` (styles/tokens/_palette.scss) and are applied by a @for loop
 * in ui/ui.scss; the array was a duplicate of that ramp that could drift from it
 * without changing a pixel, which is worse than a live literal because nothing
 * would ever show the drift. Kept in sync by name: the loop bound in ui.scss and
 * this number are the same 16.
 */
const AVATAR_COLOR_STEPS = 16;

function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getColorIndex(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return ((hash % AVATAR_COLOR_STEPS) + AVATAR_COLOR_STEPS) % AVATAR_COLOR_STEPS;
}

/**
 * UserAvatar — Deterministic colored initials avatar.
 *
 * Props:
 *   name      — Full name (used for initials + color)
 *   src       — Optional image URL (overrides initials)
 *   size      — sm | md | lg | xl
 *   bordered  — Show border + shadow
 *   className — Extra class
 */
const UserAvatar = ({ name = "", src, size = "md", bordered = false, className = "" }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`ui-avatar ui-avatar--${size} ${bordered ? "ui-avatar--bordered" : ""} ${className}`}
      />
    );
  }

  const idx = getColorIndex(name) + 1; // 1-indexed for SCSS
  return (
    <div
      className={`ui-avatar ui-avatar--${size} ui-avatar--color-${idx} ${bordered ? "ui-avatar--bordered" : ""} ${className}`}
    >
      {getInitials(name)}
    </div>
  );
};

export default UserAvatar;
